<script setup>
import {useHttpErrorsHandler} from "../../Composables/httpErrorsHandler.js";
import {useRouter} from "vue-router";
import {computed, inject, onBeforeMount, reactive, ref} from "vue";
import {useAuthUserStore} from "../../Store/AuthUserStore.js";
import {useSubscriptionStore} from "../../Store/SubscriptionStore.js";
import Loader from "../../Components/Loader.vue";
import FormTextFieldIcon from "../../Components/FormTextFieldIcon.vue";
import Modal from "../../Components/Modal.vue";
import FormSelectField from "../../Components/FormSelectField.vue";
import {useToast} from "vue-toast-notification";
const $toast = useToast();
const { httpErrorsHandler } = useHttpErrorsHandler();
const brandConfig = inject('brandConfig');
const router = useRouter();
const loading = ref(true)
const errors = ref(null)
const user = ref(null)
const authUserStore = useAuthUserStore();
const subscriptionStore = useSubscriptionStore();
const authUser = computed(() => authUserStore.authUser);
const billingEnabled = computed(() => subscriptionStore.billingEnabled);
const isAdminOrBackOffice = computed(() => authUser.value?.role === 'admin' || authUser.value?.role === 'back_office');

// Subscription management
const plans = ref([])
const selectedPlanId = ref(null)
const swapping = ref(false)
const swapErrors = ref({})
const cancelling = ref(false)
const resuming = ref(false)

const currentPlanId = computed(() => user.value?.data?.subscription?.plan?.data?.id)

const planOptions = computed(() => {
    return plans.value
        .filter(plan => plan.data.id !== currentPlanId.value)
        .map(plan => ({
            value: plan.data.id,
            text: `${plan.data.name} - ${plan.data.price_formatted}/${plan.data.billing_interval_label}`
        }))
})

function fetchPlans() {
    axios.get('/api/admin/plans')
        .then(response => {
            plans.value = response.data.data.filter(plan => plan.data.is_active)
        })
        .catch(error => {
            console.error('Failed to fetch plans:', error)
        })
}

// Historique de facturation : réservé au back-office, chargé à part car il
// interroge Stripe.
const billingHistory = ref(null)
const billingHistoryLoading = ref(false)

function fetchBillingHistory(userId) {
    // Pas de garde sur billingEnabled : le store est alimenté de façon
    // asynchrone et vaut encore false au premier affichage. L'endpoint renvoie
    // des listes vides quand la facturation est désactivée.
    if (!isAdminOrBackOffice.value) {
        return
    }

    billingHistoryLoading.value = true

    axios.get(`/api/admin/users/${userId}/billing-history`)
        .then(response => {
            billingHistory.value = response.data?.data ?? null
        })
        .catch(error => {
            console.error('Failed to fetch billing history:', error)
            billingHistory.value = null
        })
        .finally(() => {
            billingHistoryLoading.value = false
        })
}

function openSwapModal() {
    swapErrors.value = {}
    selectedPlanId.value = null
    const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('swapSubscriptionModal'))
    modal.show()
}

function swapSubscription() {
    if (!selectedPlanId.value) {
        swapErrors.value = { plan_id: ['Veuillez sélectionner un plan.'] }
        return
    }

    swapping.value = true
    swapErrors.value = {}

    axios.post(`/api/admin/users/${user.value.data.id}/subscription/swap`, {
        plan_id: selectedPlanId.value
    })
        .then(response => {
            $toast.success('Abonnement modifié avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            const modal = bootstrap.Modal.getInstance(document.getElementById('swapSubscriptionModal'))
            modal.hide()
            getUser(user.value.data.id)
        })
        .catch(error => {
            if (error.response?.status === 422) {
                swapErrors.value = error.response.data.errors || {}
            }
            httpErrorsHandler(error)
        })
        .finally(() => {
            swapping.value = false
        })
}

function openCancelModal() {
    const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('cancelSubscriptionModal'))
    modal.show()
}

function cancelSubscription() {
    cancelling.value = true

    axios.post(`/api/admin/users/${user.value.data.id}/subscription/cancel`)
        .then(() => {
            $toast.success('Abonnement annulé avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            const modal = bootstrap.Modal.getInstance(document.getElementById('cancelSubscriptionModal'))
            modal.hide()
            getUser(user.value.data.id)
        })
        .catch(error => {
            httpErrorsHandler(error)
        })
        .finally(() => {
            cancelling.value = false
        })
}

function resumeSubscription() {
    resuming.value = true

    axios.post(`/api/admin/users/${user.value.data.id}/subscription/resume`)
        .then(() => {
            $toast.success('Abonnement réactivé avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            getUser(user.value.data.id)
        })
        .catch(error => {
            httpErrorsHandler(error)
        })
        .finally(() => {
            resuming.value = false
        })
}

function getUser(userId) {
    loading.value = true;
    axios.get("/api/users/" + userId)
        .then(response => {
            user.value = response.data;
            loading.value = false;
        })
        .catch(error => {
            httpErrorsHandler(error);
            loading.value = false;
        });
}

onBeforeMount(() => {
    getUser(router.currentRoute.value.params.id)
    if (authUserStore.authUser?.role === 'admin' || authUserStore.authUser?.role === 'back_office') {
        fetchPlans()
        fetchBillingHistory(router.currentRoute.value.params.id)
    }
})

const formPassword = reactive({
    password: null,
    password_confirmation: null,
})

function updatePassword() {
    loading.value = true;
    axios.patch("/api/users/" + router.currentRoute.value.params.id + "/password", formPassword)
        .then(response => {
            $toast.success('Mot de passe modifié', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
            formPassword.password = null;
            formPassword.password_confirmation = null;
            loading.value = false;
        })
        .catch(error => {
            errors.value = error.response.data.errors;
            httpErrorsHandler(error);
            loading.value = false;
        });
}
const icon = ref('ri-eye-off-fill')
const inputTypePasswordForm = ref('password')
function showPassword() {
    if (inputTypePasswordForm.value === 'password') {
        inputTypePasswordForm.value = 'text'
        icon.value = 'ri-eye-fill'
    } else {
        inputTypePasswordForm.value = 'password'
        icon.value = 'ri-eye-off-fill'
    }
}

const activeTab = ref('centersTab')
</script>

<template>
    <div class="row">
        <div class="col-12" v-show="loading">
            <loader />
        </div>
        <div class="col-xxl-3" v-if="!loading">
            <div class="card">
                <div class="card-body p-4">
                    <div class="text-center">
                        <div class="profile-user position-relative d-inline-block mx-auto  mb-4">
                            <img :src="'/format-image/'+user.data.profile_picture+'?w=110&h=110&fm=webp&fit=crop'"  class="rounded-circle avatar-xl img-thumbnail user-profile-image  shadow" alt="user-profile-image">
                        </div>
                        <h5 class="fs-16 mb-1">{{ user.data.first_name + ' ' + user.data.last_name }}</h5>
                        <p :class="'badge fs-11 bg-'+user.data.role_badge_color+'-subtle  text-'+user.data.role_badge_color">{{ user.data.role_label_fr }}</p>
                        <ul class="list-inline mb-0">
                            <li class="list-inline-item avatar-xs" title="Appeler">
                                <a :href="'tel:'+user.data.phone_number" class="avatar-title bg-success-subtle text-success fs-15 rounded">
                                    <i class="ri-phone-line"></i>
                                </a>
                            </li>
                            <li class="list-inline-item avatar-xs" title="Envoyer un email" v-if="user.data.email !== null">
                                <a :href="'mailto:'+user.data.email" class="avatar-title bg-warning-subtle text-warning fs-15 rounded">
                                    <i class="ri-mail-line"></i>
                                </a>
                            </li>
                            <li class="list-inline-item avatar-xs" title="Modifier" v-if="user.can.update">
                                <router-link :to="{ name: 'users.edit', params:{ id: user.data.id } }" class="avatar-title bg-secondary-subtle text-secondary fs-15 rounded" v-if="authUser.role === 'admin' || authUser.role === 'back_office'">
                                    <i class="ri-edit-2-fill"></i>
                                </router-link>
                                <router-link :to="{ name: 'users.profile' }" class="avatar-title bg-secondary-subtle text-secondary fs-15 rounded" v-else>
                                    <i class="ri-edit-2-fill"></i>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div class="card">
                <div class="card-header align-items-center d-flex">
                    <h4 class="card-title mb-0 flex-grow-1">
                        <i class="ri-information-line me-2 align-middle fs-4"></i>
                        Info
                    </h4>
                </div>
                <div class="card-body">
                    <div class="table-responsive">
                        <table class="table table-borderless mb-0">
                            <tbody>
                            <tr>
                                <th class="ps-0" scope="row">Téléphone&nbsp;:</th>
                                <td class="text-body">
                                    <a :href="'tel:'+user.data.phone_number" class="text-secondary">{{ user.data.phone_number }}</a>
                                </td>
                            </tr>
                            <tr>
                                <th class="ps-0" scope="row">E-mail&nbsp;:</th>
                                <td class="text-body">
                                    <a :href="'mailto:'+user.data.email" class="text-secondary">{{ user.data.email }}</a>
                                </td>
                            </tr>
                            <tr>
                                <th class="ps-0" scope="row">Équipe de direction&nbsp;:</th>
                                <td class="text-body">
                                    {{ user.data.management_team ? 'Oui' : 'Non' }}
                                </td>
                            </tr>
                            <tr>
                                <th class="ps-0" scope="row">Date de naissance&nbsp;:</th>
                                <td class="text-body">{{ user.data.birth_date_fr }}</td>
                            </tr>
                            <tr>
                                <th class="ps-0" scope="row">Créé le</th>
                                <td class="text-body">{{ user.data.created_at_fr }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-xxl-9" v-if="!loading">
            <div class="card">
                <div class="card-header align-items-center d-flex">
                    <h4 class="card-title mb-0 flex-grow-1">
                        <i class="ri-user-line me-2 align-middle fs-4"></i>
                        A propos
                    </h4>
                </div>
                <div class="card-body">
                    <p v-html="user.data.bio === null ? 'Aucune description' : user.data.bio.replace(/\n/g, '<br />')" class="mb-3"></p>
                    <div class="row">
                        <div class="col-6 col-md-4">
                            <div class="d-flex mt-2">
                                <div class="flex-shrink-0 avatar-xs align-self-center me-3">
                                    <div class="avatar-title bg-light rounded-circle fs-16 text-secondary shadow">
                                        <i class="ri-user-2-fill"></i>
                                    </div>
                                </div>
                                <div class="flex-grow-1 overflow-hidden">
                                    <p class="mb-1">Poste occupé :</p>
                                    <h6 class="text-truncate mb-0">{{ user.data.job_title === null ? '--' : user.data.job_title }}</h6>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card">
                <div class="card-header">
                    <ul class="nav nav-tabs nav-tabs-custom card-header-tabs border-bottom-0 nav-secondary text-dark" role="tablist">
                        <li class="nav-item" @click.prevent="activeTab = 'centersTab'">
                            <a class="nav-link" :class="{ 'active': activeTab === 'centersTab' }" data-bs-toggle="tab" href="#centersTab" role="tab">
                                <i class="fas fa-home"></i> {{ user.data.centers.length > 1 ? 'Centres' : 'Centre' }}
                            </a>
                        </li>
                        <li class="nav-item" @click.prevent="activeTab = 'changePassword'">
                            <a class="nav-link" :class="{ 'active': activeTab === 'changePassword' }" data-bs-toggle="tab" href="#changePassword" role="tab">
                                <i class="far fa-user"></i> Mot de passe
                            </a>
                        </li>
                        <li class="nav-item" @click.prevent="activeTab = 'subscriptionTab'" v-if="billingEnabled">
                            <a class="nav-link" :class="{ 'active': activeTab === 'subscriptionTab' }" data-bs-toggle="tab" href="#subscriptionTab" role="tab">
                                <i class="ri-vip-crown-line"></i> Abonnement
                            </a>
                        </li>
                    </ul>
                </div>
                <div class="card-body p-4">
                    <div class="tab-content">
                        <div class="tab-pane" :class="{ 'active': activeTab === 'centersTab' }" id="centersTab" role="tabpanel">
                            <ul class="list-group list-group-flush">

                                <router-link :to="{ name: 'centers.show', params:{ id: center.data.id } }" class="list-group-item" v-for="(center, index) in user.data.centers" :key="index">
                                    <div class="d-flex align-items-center">
                                        <div class="flex-shrink-0">
                                            <img :src="brandConfig.logoBlackSmall" alt="" class="avatar-xs">
                                        </div>
                                        <div class="flex-grow-1 ms-2 name">{{ center.data.name }}</div>
                                    </div>
                                </router-link>

                            </ul>
                        </div>

                        <div class="tab-pane" :class="{ 'active': activeTab === 'changePassword' }" id="changePassword" role="tabpanel">
                            <form v-on:submit.prevent="updatePassword">
                                <div class="row">
                                    <form-text-field-icon
                                        name="password"
                                        label="Nouveau mot de passe"
                                        :label-hidden="false"
                                        @update:field="formPassword.password = $event"
                                        :errors="errors"
                                        :required="false"
                                        :data="formPassword.password"
                                        bs-class="col-lg-6"
                                        :type="inputTypePasswordForm"
                                        :icon-class="icon"
                                        icon-description="Cliquez pour afficher/masquer"
                                        @clic:icon="showPassword()"
                                        autocomplete="new-password"
                                        :tabindex="2"
                                    />

                                    <form-text-field-icon
                                        name="password_confirmation"
                                        label="Confirmer"
                                        :label-hidden="false"
                                        @update:field="formPassword.password_confirmation = $event"
                                        :errors="errors"
                                        :required="false"
                                        :data="formPassword.password_confirmation"
                                        bs-class="col-lg-6"
                                        :type="inputTypePasswordForm"
                                        :icon-class="icon"
                                        icon-description="Cliquez pour afficher/masquer"
                                        @clic:icon="showPassword()"
                                        autocomplete="new-password"
                                        :tabindex="3"
                                    />

                                    <div class="col-lg-12">
                                        <div class="d-flex gap-2 justify-content-end">
                                            <button type="submit" class="btn btn-secondary">Valider</button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>

                        <div class="tab-pane" :class="{ 'active': activeTab === 'subscriptionTab' }" id="subscriptionTab" role="tabpanel" v-if="billingEnabled">
                            <div v-if="user.data.subscription">
                                <div class="table-responsive">
                                    <table class="table table-borderless mb-0">
                                        <tbody>
                                            <tr>
                                                <th class="ps-0" scope="row">Plan&nbsp;:</th>
                                                <td class="text-body fw-semibold">{{ user.data.subscription.plan?.data?.name }}</td>
                                            </tr>
                                            <tr>
                                                <th class="ps-0" scope="row">Statut&nbsp;:</th>
                                                <td>
                                                    <span v-if="user.data.subscription.active && !user.data.subscription.canceled" class="badge bg-success-subtle text-success">Actif</span>
                                                    <span v-else-if="user.data.subscription.on_grace_period" class="badge bg-warning-subtle text-warning fs-11">Annulé (actif jusqu'au {{ user.data.subscription.ends_at }})</span>
                                                    <span v-else class="badge bg-danger-subtle text-danger">Inactif</span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <th class="ps-0" scope="row">Date de souscription&nbsp;:</th>
                                                <td class="text-body">{{ user.data.subscription.created_at }}</td>
                                            </tr>
                                            <tr v-if="user.data.subscription.ends_at">
                                                <th class="ps-0" scope="row">Date de fin&nbsp;:</th>
                                                <td class="text-body">{{ user.data.subscription.ends_at }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div class="mt-3 d-flex gap-2 flex-wrap" v-if="isAdminOrBackOffice && user.data.subscription.active">
                                    <button type="button" class="btn btn-soft-secondary d-inline-flex align-items-center" @click="openSwapModal">
                                        <i class="ri-exchange-line me-2"></i> Changer d'abonnement
                                    </button>
                                    <button
                                        v-if="user.data.subscription.on_grace_period"
                                        type="button"
                                        class="btn btn-soft-success d-inline-flex align-items-center"
                                        :disabled="resuming"
                                        @click="resumeSubscription"
                                    >
                                        <span v-if="resuming" class="spinner-border spinner-border-sm me-2"></span>
                                        <i v-else class="ri-restart-line me-2"></i> Réactiver
                                    </button>
                                    <button
                                        v-else
                                        type="button"
                                        class="btn btn-soft-danger d-inline-flex align-items-center"
                                        @click="openCancelModal"
                                    >
                                        <i class="ri-close-circle-line me-2"></i> Annuler
                                    </button>
                                </div>
                            </div>
                            <div v-else class="text-center py-4">
                                <i class="ri-vip-crown-line fs-1 text-muted"></i>
                                <p class="text-muted mt-2 mb-2">Aucun abonnement actif</p>
                                <span
                                    v-if="user.data.subscription_status && user.data.subscription_status !== 'none'"
                                    :class="'fs-11 badge bg-'+user.data.subscription_status_badge_color+'-subtle text-'+user.data.subscription_status_badge_color"
                                >
                                    Dernier abonnement : {{ user.data.subscription_status_label_fr }}
                                </span>
                            </div>

                            <!-- Historique de facturation, back-office uniquement -->
                            <div v-if="isAdminOrBackOffice" class="mt-4 pt-4 border-top">
                                <h5 class="fs-15 mb-3">Historique de facturation</h5>

                                <div v-if="billingHistoryLoading" class="text-center py-4">
                                    <div class="spinner-border text-secondary" role="status">
                                        <span class="visually-hidden">Chargement...</span>
                                    </div>
                                </div>

                                <div v-else-if="billingHistory">
                                    <!-- Synthèse : bloc encadré plutôt que des cartes, qui
                                         seraient blanches sur blanc dans ce panneau.
                                         Pas de border-dashed ici : la classe force le style
                                         sur les quatre côtés et dessine un cadre parasite. -->
                                    <div class="border rounded mb-4 billing-summary" v-if="billingHistory.summary">
                                        <div class="row g-0">
                                            <div class="col-md-4 border-end">
                                                <div class="p-3 d-flex align-items-center">
                                                    <div class="avatar-sm flex-shrink-0 me-3">
                                                        <span class="avatar-title bg-info-subtle text-info rounded-circle fs-3">
                                                            <i class="ri-calendar-check-line"></i>
                                                        </span>
                                                    </div>
                                                    <div class="flex-grow-1 overflow-hidden">
                                                        <p class="text-uppercase fw-medium text-muted text-truncate fs-12 mb-1">Client depuis</p>
                                                        <h5 class="fs-16 fw-semibold ff-secondary mb-0">
                                                            {{ billingHistory.summary.first_subscribed_at || '—' }}
                                                        </h5>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-md-4 border-end">
                                                <div class="p-3 d-flex align-items-center">
                                                    <div class="avatar-sm flex-shrink-0 me-3">
                                                        <span class="avatar-title bg-success-subtle text-success rounded-circle fs-3">
                                                            <i class="ri-money-euro-circle-line"></i>
                                                        </span>
                                                    </div>
                                                    <div class="flex-grow-1 overflow-hidden">
                                                        <p class="text-uppercase fw-medium text-muted text-truncate fs-12 mb-1">Dernier paiement</p>
                                                        <h5 class="fs-16 fw-semibold ff-secondary mb-0">
                                                            {{ billingHistory.summary.last_payment_at || '—' }}
                                                        </h5>
                                                        <p class="text-muted fs-12 mb-0" v-if="billingHistory.summary.last_payment_amount">
                                                            {{ billingHistory.summary.last_payment_amount }}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="p-3 d-flex align-items-center">
                                                    <div class="avatar-sm flex-shrink-0 me-3">
                                                        <span class="avatar-title bg-danger-subtle text-danger rounded-circle fs-3">
                                                            <i class="ri-error-warning-line"></i>
                                                        </span>
                                                    </div>
                                                    <div class="flex-grow-1 overflow-hidden">
                                                        <p class="text-uppercase fw-medium text-muted text-truncate fs-12 mb-1">Dernier échec</p>
                                                        <h5
                                                            class="fs-16 fw-semibold ff-secondary mb-0"
                                                            :class="{ 'text-danger': billingHistory.summary.last_failed_payment_at }"
                                                        >
                                                            {{ billingHistory.summary.last_failed_payment_at || '—' }}
                                                        </h5>
                                                        <p class="text-muted fs-12 mb-0" v-if="billingHistory.summary.last_failed_amount">
                                                            {{ billingHistory.summary.last_failed_amount }}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Interruption : sert à chiffrer une facture rétroactive -->
                                    <div
                                        class="alert alert-warning d-flex align-items-center mb-4"
                                        role="alert"
                                        v-if="billingHistory.summary && billingHistory.summary.interrupted_since"
                                    >
                                        <i class="ri-time-line fs-16 me-2 align-middle"></i>
                                        <div>
                                            Abonnement interrompu depuis le <strong>{{ billingHistory.summary.interrupted_since }}</strong>,
                                            soit <strong>{{ billingHistory.summary.interruption_months }} mois</strong> non couverts.
                                        </div>
                                    </div>

                                    <!-- Périodes d'abonnement -->
                                    <h6 class="fs-14 mb-2">Périodes d'abonnement</h6>
                                    <div class="table-responsive mb-4">
                                        <table class="table table-md table-nowrap align-middle mb-0">
                                            <thead class="table-light">
                                                <tr>
                                                    <th scope="col">Plan</th>
                                                    <th scope="col">Statut</th>
                                                    <th scope="col">Début</th>
                                                    <th scope="col">Fin</th>
                                                    <th scope="col">Durée</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="period in billingHistory.subscriptions" :key="period.id">
                                                    <td class="fw-medium">{{ period.plan_name || '—' }}</td>
                                                    <td>
                                                        <span :class="'fs-11 badge bg-'+period.status_badge_color+'-subtle text-'+period.status_badge_color">
                                                            {{ period.status_label_fr }}
                                                        </span>
                                                    </td>
                                                    <td>{{ period.started_at }}</td>
                                                    <td>
                                                        <span v-if="period.ends_at">{{ period.ends_at }}</span>
                                                        <span v-else class="text-muted">en cours</span>
                                                    </td>
                                                    <td>{{ period.months_covered }} mois</td>
                                                </tr>
                                                <tr v-if="!billingHistory.subscriptions.length">
                                                    <td colspan="5" class="text-center text-muted py-3">Aucune souscription</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <!-- Factures Stripe -->
                                    <h6 class="fs-14 mb-2">Factures</h6>
                                    <div class="alert alert-warning d-flex align-items-center mb-0" role="alert" v-if="billingHistory.invoices_available === false">
                                        <i class="ri-cloud-off-line fs-16 me-2 align-middle"></i>
                                        <div>Factures indisponibles, Stripe n'a pas répondu.</div>
                                    </div>
                                    <div class="table-responsive" v-else>
                                        <table class="table table-md table-nowrap align-middle mb-0">
                                            <thead class="table-light">
                                                <tr>
                                                    <th scope="col">Date</th>
                                                    <th scope="col">Numéro</th>
                                                    <th scope="col">Montant</th>
                                                    <th scope="col">Statut</th>
                                                    <th scope="col" class="text-end">Facture</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="invoice in billingHistory.invoices" :key="invoice.number">
                                                    <td>{{ invoice.date }}</td>
                                                    <td class="fw-medium">{{ invoice.number || '—' }}</td>
                                                    <td>{{ invoice.total }}</td>
                                                    <td>
                                                        <span v-if="invoice.is_paid" class="fs-11 badge bg-success-subtle text-success">Payée</span>
                                                        <span v-else class="fs-11 badge bg-danger-subtle text-danger">
                                                            Impayée
                                                            <template v-if="invoice.attempt_count"> · {{ invoice.attempt_count }} tentatives</template>
                                                        </span>
                                                    </td>
                                                    <td class="text-end">
                                                        <a
                                                            v-if="invoice.hosted_url"
                                                            :href="invoice.hosted_url"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            class="btn btn-sm btn-soft-secondary"
                                                        >
                                                            <i class="ri-external-link-line align-bottom"></i>
                                                        </a>
                                                    </td>
                                                </tr>
                                                <tr v-if="!billingHistory.invoices.length">
                                                    <td colspan="5" class="text-center text-muted py-3">Aucune facture</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal Swap Subscription -->
    <modal
        id="swapSubscriptionModal"
        title="Changer d'abonnement"
        btn-text="Confirmer"
        btn-class="btn-secondary"
        :loading="swapping"
        @click:button="swapSubscription"
    >
        <template #icon>
            <lord-icon
                src="https://cdn.lordicon.com/iltqorsz.json"
                trigger="loop"
                colors="primary:#405189,secondary:#0ab39c"
                style="width:90px;height:90px">
            </lord-icon>
        </template>
        <template #content>
            <p class="text-muted fs-14 mb-4">Sélectionnez le nouveau plan pour cet utilisateur.</p>
            <form-select-field
                name="plan_id"
                label="Plan"
                :label-hidden="true"
                :liste-options="planOptions"
                :data="selectedPlanId"
                :errors="swapErrors"
                :required="true"
                :disabled="swapping"
                placeholder="Sélectionner un plan"
                bs-class="col-12"
                margin="mb-4"
                @update:field="selectedPlanId = $event"
            />
        </template>
    </modal>

    <!-- Modal Cancel Subscription -->
    <modal
        id="cancelSubscriptionModal"
        title="Annuler l'abonnement"
        btn-text="Confirmer l'annulation"
        btn-class="btn-danger"
        :loading="cancelling"
        @click:button="cancelSubscription"
    >
        <template #icon>
            <lord-icon
                src="https://cdn.lordicon.com/gsqxdxog.json"
                trigger="loop"
                colors="primary:#212529,secondary:#f06548"
                style="width:90px;height:90px">
            </lord-icon>
        </template>
        <template #content>
            <p class="text-muted fs-14 mb-4">
                Êtes-vous sûr de vouloir annuler l'abonnement de cet utilisateur ?
                L'utilisateur conservera l'accès jusqu'à la fin de la période en cours.
            </p>
        </template>
    </modal>
</template>

<style scoped>
/* Les colonnes de la synthèse s'empilent sous md : le séparateur doit alors
   passer d'une bordure droite à une bordure basse. */
@media (max-width: 767.98px) {
    .billing-summary .border-end {
        border-right: 0 !important;
        border-bottom: var(--vz-border-width, 1px) solid var(--vz-border-color, #e9ebec);
    }
}
</style>
